import { useQueryClient } from "@tanstack/react-query";
import { useMesfRealtime } from "@dexteel/mesf-core";

/**
 * Maps READ stored procedure names (targets) to TanStack Query key prefixes.
 *
 * The backend flow is:
 *   1. Frontend calls a WRITE SP (e.g. [EAF].[UpsertProcess])
 *   2. SpInvalidationService looks up QueryCacheInvalidations for that source
 *   3. Broadcasts each TARGET SP name (e.g. "RBD.GetGanttData") via SignalR
 *   4. This hook receives the target name and invalidates matching queries
 */
const SP_TO_QUERY_KEYS: Record<string, readonly string[][]> = {
  "RBD.GetGanttData": [["dashboard-gantt"]],
  "RBD.GetDashboardEAFProduction": [["dashboard-eaf-production"]],
  "RBD.GetDashboardLMFProduction": [["dashboard-lmf-production"]],
  "RBD.GetDashboardCASProduction": [["dashboard-cas-production"]],
};

/**
 * Hook that listens to SignalR messages and invalidates TanStack Query cache.
 * Enables real-time dashboard updates when any client or PLC modifies data.
 *
 * Usage: Call once at the DashboardPage level.
 */
export const useRealtimeInvalidation = () => {
  const queryClient = useQueryClient();

  useMesfRealtime({
    onReceiveMessage: (_author: string, message: string) => {
      const queryKeys = SP_TO_QUERY_KEYS[message];
      if (queryKeys) {
        for (const queryKey of queryKeys) {
          queryClient.invalidateQueries({ queryKey });
        }
      }
    },
  });
};
